import {Row, Col} from 'react-bootstrap';
import Button from '@/components/ui/Button';
import React, {useState, useEffect} from 'react';
import axios from 'axios';
import i18n from 'i18n';
import CellLoader from '@/components/loading/CellLoader';  
import {buildURL} from '@/utils/urlBuilder';
import {navigate} from '@reach/router';
import {useStoreState} from '@/store';

interface SwitchViewData {
    agentViewSelected: boolean;
    currentUserAgentId: string | null;
}

interface WindowWithURLs extends Window {
    URLS: {
        switchRoleResource: string;
        switchViewResource: string;
        specialStoryDetailView: string;
    };
}

declare const window: WindowWithURLs;

const SwitchRoleSettingsView: React.FC = React.memo(() => {
    const [switchViewData, setSwitchViewData] = useState<SwitchViewData | null>(null);
    const [loading, setLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    
    // Get user data from store
    const userData = useStoreState(state => state.userData.data);
    const menuStructure = useStoreState(state => state.sidebarMenu.menuStructure);

    useEffect(() => {
        const loadSwitchViewData = async () => {
            try {
                const response = await axios.get(window.URLS.switchViewResource);
                setSwitchViewData(response.data);
            } catch (error) {
                console.error('Error loading switch view data:', error);
                // Fallback to default values
                setSwitchViewData({
                    agentViewSelected: false,
                    currentUserAgentId: null
                });
            } finally {
                setLoading(false);
            }
        };

        loadSwitchViewData();
    }, []);

    const switchRole = async (typeOfSwitch: 'agent' | 'default'): Promise<void> => {
        if (isProcessing) { return; }

        setIsProcessing(true);
        try {
            await axios.post(window.URLS.switchRoleResource);

            if (typeOfSwitch === 'agent') {
                navigate(buildURL('specialStoryDetailView', {story_slug: switchViewData?.currentUserAgentId || ''}));
            } else {
                const {getRootRedirectUrl} = await import('../../utils/rootUrlResolver');
                navigate(getRootRedirectUrl({userData, menuStructure}));
            }
        } catch (error) {
            console.error('Error switching role:', error);
            setIsProcessing(false);
        }
    };

    if (loading) {
        return <CellLoader />;
    }

    const hasAgentId = switchViewData?.currentUserAgentId && switchViewData.currentUserAgentId !== 'None';
    const isAgentViewActive = switchViewData?.agentViewSelected;
    const analyticsButtonDisplay = hasAgentId ? (
        isAgentViewActive ? (
            <Button
                onClick={() => switchRole('default')}
                variant="info"
                size="sm"
                disabled={isProcessing}
            >
                {i18n.t('jt_activate.text')}
            </Button>
        ) : (
            <Button
                variant="secondary"
                size="sm"
                disabled
                style={{pointerEvents: 'none'}}
                data-cy="switch-role-settings-btn-2">
                {i18n.t('jt_active.text')}
            </Button>
        )
    ) : (
        <Button
            variant="secondary"
            size="sm"
            disabled
            style={{pointerEvents: 'none'}}
            data-cy="switch-role-settings-btn-1">
            {i18n.t('jt_activated.text')}
        </Button>
    );

    const agentButtonDisplay = hasAgentId ? (
        isAgentViewActive ? (
            <Button
                variant="secondary"
                size="sm"
                disabled
                style={{pointerEvents: 'none'}}
                data-cy="switch-role-settings-const-agentbuttondisplay-btn">
                {i18n.t('jt_active.text')}
            </Button>
        ) : (
            <Button
                onClick={() => switchRole('agent')}
                variant="info"
                size="sm"
                disabled={isProcessing}
            >
                {i18n.t('jt_activate.text')}
            </Button>
        )
    ) : (
        <div className="alert alert-warning text-left h-30">
            {i18n.t('jt_agent_view.text')}
        </div>
    );

    return (
        <div className="container-fluid">
            <div className="mb-4">
                <h2>{i18n.t('jt_switch_your.text')}</h2>
                <p className="text-muted">
                    Switch between analytics view and agent personal view.
                </p>
            </div>

            <Row>
                <Col xs={6}>
                    <div className="card w-100">
                        <div className="card-body">
                            <div className="">
                                <h4 className="card-title">{i18n.t('jt_analytics_view.text')}</h4>
                                <p>{i18n.t('jt_activate_analytics.text')}</p>
                                {analyticsButtonDisplay}
                            </div>
                        </div>
                    </div>
                </Col>
                <Col xs={6}>
                    <div className="card w-100">
                        <div className="card-body">
                            <div className="">
                                <h4 className="card-title">{i18n.t('jt_agent_personal.text')}</h4>
                                <p>{i18n.t('jt_this_the.text')}</p>
                                {agentButtonDisplay}
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>


        </div>
    );
});

export default SwitchRoleSettingsView;
